import { useCallback, useEffect, useRef, useState } from "react";
import { liveApi, KINDS } from "@/lib/simranLive";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Plays one text through an <audio> element and resolves when it ends (or after a reading pause if the voice fails).
const useSpeaker = (lang, onCaption) => {
  const audio = useRef(typeof Audio !== "undefined" ? new Audio() : null);
  const cache = useRef(new Map());
  const [t, setT] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const voice = useCallback((text) => {
    if (!cache.current.has(text)) cache.current.set(text, liveApi.tts(text, lang).catch(() => null));
    return cache.current.get(text);
  }, [lang]);
  useEffect(() => {
    const a = audio.current;
    const tick = () => setT(a.currentTime);
    const on = () => setSpeaking(true), off = () => setSpeaking(false);
    a.addEventListener("timeupdate", tick); a.addEventListener("playing", on); a.addEventListener("pause", off); a.addEventListener("ended", off);
    return () => { a.pause(); a.removeEventListener("timeupdate", tick); a.removeEventListener("playing", on); a.removeEventListener("pause", off); a.removeEventListener("ended", off); };
  }, []);
  const speak = useCallback(async (text, isLive) => {
    const v = await voice(text);
    if (!isLive()) return;
    const a = audio.current;
    onCaption({ text, marks: v?.marks || [] });
    if (!v) { await sleep(Math.max(3000, text.split(/\s+/).length * 420)); return; }
    a.src = `data:audio/mpeg;base64,${v.audio}`;
    await new Promise((res) => { a.onended = res; a.play().catch(() => res()); });
  }, [voice, onCaption]);
  return { audio, t, speaking, speak, voice };
};

export const useLiveLecture = (lang, greet) => {
  const [lec, setLec] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | live | check | done
  const [n, setN] = useState(0);
  const [sec, setSec] = useState(null);
  const [check, setCheck] = useState(null);
  const [cap, setCap] = useState({ text: "", marks: [] });
  const [writing, setWriting] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState("");
  const run = useRef(0);
  const cont = useRef(null);
  const hold = useRef(false);
  const cur = useRef(null);
  const sp = useSpeaker(lec?.lang || lang, setCap);
  const say = useCallback(async (text, isLive) => {
    while (hold.current && isLive()) await sleep(250);
    if (isLive()) await sp.speak(text, isLive);
  }, [sp]);

  const play = useCallback(async (L, from = 0) => {
    const id = ++run.current;
    const isLive = () => run.current === id;
    setPhase("live"); setError(""); setPaused(false); hold.current = false;
    try {
      let next = liveApi.section(L.id, from, 0);
      if (from === 0) { setSec({ kind: "greet", title: L.title, points: [] }); await say(greet(L), isLive); }
      for (let i = from; i < KINDS.length && isLive(); i++) {
        setN(i);
        setWriting(true);
        let s = await next;
        setWriting(false);
        if (i === 1) liveApi.status(L.id).then((x) => isLive() && setLec((p) => (p ? { ...p, title: x.title } : p))).catch(() => {});
        next = i + 1 < KINDS.length ? liveApi.section(L.id, i + 1, 0) : null;
        if (!isLive()) return;
        if (s.kind === "check") {
          setCheck({ ...s, n: i, result: null }); setPhase("check");
          await new Promise((res) => { cont.current = res; say(s.question, isLive); });
          setCheck(null); setPhase("live");
          continue;
        }
        setSec(s);
        for (let k = 0; isLive(); k++) {
          while (k >= s.paras.length && !s.done) { setWriting(true); s = await liveApi.section(L.id, i, s.paras.length); setSec(s); }
          setWriting(false);
          if (k >= s.paras.length) break;
          if (s.paras[k + 1]) sp.voice(s.paras[k + 1]);
          await say(s.paras[k], isLive);
        }
      }
      if (isLive()) { setPhase("done"); setLec(await liveApi.status(L.id)); }
    } catch (e) {
      if (isLive()) { setWriting(false); setError(e?.response?.data?.detail || "failed"); }
    }
  }, [greet, sp, say]);

  const start = (L) => { cur.current = L.id; setLec(L); setN(0); play(L, 0); };
  const retry = () => lec && play(lec, n);
  const pause = () => { hold.current = true; sp.audio.current.pause(); setPaused(true); };
  const resume = () => { hold.current = false; setPaused(false); const a = sp.audio.current; if (a.src && !a.ended && a.currentTime > 0) a.play().catch(() => {}); };
  const stop = () => { run.current += 1; sp.audio.current.pause(); if (cur.current) liveApi.end(cur.current); cur.current = null; setPhase("idle"); setLec(null); setSec(null); setCheck(null); };
  const answer = async (pick) => {
    const r = await liveApi.pick(lec.id, check.n, pick);
    setCheck((c) => ({ ...c, result: { ...r, pick } }));
    const id = run.current;
    sp.speak(r.explain, () => run.current === id);
  };
  const next = () => { sp.audio.current.pause(); cont.current?.(); };
  useEffect(() => {
    const leave = () => cur.current && liveApi.beaconEnd(cur.current);
    window.addEventListener("pagehide", leave);
    return () => { window.removeEventListener("pagehide", leave); run.current += 1; if (cur.current) liveApi.end(cur.current); };
  }, []);
  return { lec, phase, n, sec, check, cap, writing, paused, error, t: sp.t, speaking: sp.speaking, start, retry, pause, resume, stop, answer, next, replay: () => lec && play(lec, 0) };
};
